/**
 * Load More Pagination for Shopify Collections
 * Fixed version - Properly handles filter/sort reloads
 */

class LoadMorePagination {
  constructor() {
    this.loadMoreBtn = document.querySelector('.load-more-btn');
    this.productGrid = document.querySelector('#main-collection-product-grid');
    this.paginationContainer = document.querySelector('.pagination-load-more');
    this.isLoading = false;
    this.currentPage = 1;
    this.totalPages = null;
    
    if (this.loadMoreBtn && this.productGrid) {
      this.init();
    }
  }

  init() {
    // Clean URL on page load - remove page parameter only
    this.cleanUrlOnLoad();
    
    // Remove any existing event listeners to prevent duplicates
    const newButton = this.loadMoreBtn.cloneNode(true);
    this.loadMoreBtn.parentNode.replaceChild(newButton, this.loadMoreBtn);
    this.loadMoreBtn = newButton;
    
    // Add click event listener
    this.loadMoreBtn.addEventListener('click', () => this.loadMore());
    
    // Get pagination info from data attributes
    if (this.paginationContainer) {
      const totalPagesAttr = this.paginationContainer.getAttribute('data-total-pages');
      
      // Always start from page 1, ignore URL parameter
      this.totalPages = totalPagesAttr ? parseInt(totalPagesAttr) : null;
      this.currentPage = 1;
      
      console.log('Pagination initialized:', {
        currentPage: this.currentPage,
        totalPages: this.totalPages,
        currentUrl: window.location.href
      });
    }
    
    // Initial check for more pages
    this.checkIfLastPage();
  }

  getCurrentUrlParams() {
    // Get all current URL parameters except 'page'
    const params = new URLSearchParams(window.location.search);
    params.delete('page'); // Always remove page parameter
    return params;
  }

  cleanUrlOnLoad() {
    // Remove page parameter from URL on page load/refresh
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.has('page')) {
      // Remove page parameter while keeping all other params
      urlParams.delete('page');
      
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      
      // Replace URL without page reload
      window.history.replaceState({}, '', newUrl);
      
      console.log('URL cleaned - page parameter removed');
    }
  }

  checkIfLastPage() {
    // If we know the total pages, check directly
    if (this.totalPages !== null) {
      if (this.currentPage >= this.totalPages) {
        console.log('Last page reached:', this.currentPage, '>=', this.totalPages);
        this.hideLoadMoreButton();
        return true;
      } else {
        this.showLoadMoreButton();
        return false;
      }
    }
    
    // Otherwise, check for next page link
    const hasNext = this.paginationContainer?.getAttribute('data-has-next');
    if (hasNext === 'false' || !hasNext) {
      console.log('Last page reached (no next URL)');
      this.hideLoadMoreButton();
      return true;
    }
    
    this.showLoadMoreButton();
    return false;
  }

  async loadMore() {
    if (this.isLoading) return;
    
    // Check if we're already on the last page
    if (this.checkIfLastPage()) {
      return;
    }

    this.isLoading = true;
    this.loadMoreBtn.classList.add('loading');
    this.loadMoreBtn.textContent = 'Loading...';
    this.loadMoreBtn.disabled = true;

    try {
      const nextPage = this.currentPage + 1;
      
      // Final check before loading
      if (this.totalPages !== null && nextPage > this.totalPages) {
        console.log('Cannot load page', nextPage, '- exceeds total pages:', this.totalPages);
        this.hideLoadMoreButton();
        return;
      }
      
      // Build URL with all current filters/sorting + page parameter
      const url = this.buildPageUrl(nextPage);
      
      console.log('Loading page:', nextPage, 'of', this.totalPages || '?');
      console.log('Fetch URL:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get new products from the fetched page
      const newProducts = doc.querySelectorAll('#main-collection-product-grid > *');
      
      console.log('Found', newProducts.length, 'new products');
      
      if (newProducts.length > 0) {
        // Append new products to the grid
        newProducts.forEach(product => {
          this.productGrid.appendChild(product.cloneNode(true));
        });
   
        // Update current page (but don't update URL)
        this.currentPage = nextPage;
        

      
        // DO NOT update URL - keep it clean without page parameter
        // URL stays with only filters/sorting, no page number
        
        // Check if this was the last page
        const isLastPage = this.totalPages !== null ? 
          (nextPage >= this.totalPages) : 
          await this.checkForMorePages(nextPage);
        
        if (isLastPage) {
          console.log('Last page reached after loading page', nextPage);
          this.hideLoadMoreButton();
        } else {
          this.showLoadMoreButton();
        }
        
        // Reinitialize any necessary scripts
        setTimeout(() => {
        this.reinitializeScripts();

        }, 100);
        
        // Trigger MutationObserver for parallax effects if needed
        if (window.prlxPush) {
          const collectionGrid = document.getElementById('CollectionProductGrid');
          if (collectionGrid) {
            window.prlxPush(collectionGrid.querySelectorAll('[data-js-prlx-parent]'));
          }
        }
        
        // Scroll smoothly to show new products
        if (newProducts.length > 0) {
          setTimeout(() => {
            const firstNewProduct = this.productGrid.children[this.productGrid.children.length - newProducts.length];
            if (firstNewProduct) {
              firstNewProduct.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 100);
        }
        
      } else {
        console.log('No products found on page', nextPage, '- hiding button');
        this.hideLoadMoreButton();
      }
      
    } catch (error) {
      console.error('Error loading more products:', error);
      this.loadMoreBtn.textContent = 'Error - Click to retry';
      this.loadMoreBtn.disabled = false;
      this.loadMoreBtn.classList.remove('loading');
    } finally {
      this.isLoading = false;
      
      if (this.loadMoreBtn && !this.loadMoreBtn.disabled) {
        this.loadMoreBtn.classList.remove('loading');
        this.loadMoreBtn.textContent = this.loadMoreBtn.getAttribute('data-original-text') || 'Load more';
      }
    }
  }

  buildPageUrl(page) {
    // Build URL with all current parameters + page number
    const params = this.getCurrentUrlParams();
    params.set('page', page);
    
    // Construct full URL
    const url = window.location.pathname + '?' + params.toString();
    
    return url;
  }

  async checkForMorePages(currentPage) {
    try {
      // If we know total pages, use that
      if (this.totalPages !== null) {
        return currentPage < this.totalPages;
      }
      
      // Otherwise, try to fetch the next page to see if it exists
      const testPage = currentPage + 1;
      const url = this.buildPageUrl(testPage);
      
      const response = await fetch(url, { method: 'HEAD' }); // Use HEAD for efficiency
      
      if (!response.ok) {
        return false;
      }
      
      // Do a full fetch to check for products
      const fullResponse = await fetch(url);
      const html = await fullResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Check if there are products on the next page
      const products = doc.querySelectorAll('#main-collection-product-grid > *');
      
      return products.length > 0;
    } catch (error) {
      console.error('Error checking for more pages:', error);
      return false;
    }
  }

  hideLoadMoreButton() {
    if (this.paginationContainer) {
      this.paginationContainer.style.display = 'none';
      console.log('Load more button hidden');
    }
  }

  showLoadMoreButton() {
    if (this.paginationContainer) {
      this.paginationContainer.style.display = 'flex';
      if (this.loadMoreBtn) {
        this.loadMoreBtn.textContent = this.loadMoreBtn.getAttribute('data-original-text') || 'Load more';
        this.loadMoreBtn.disabled = false;
        this.loadMoreBtn.classList.remove('loading');
      }

    }
  }

  reinitializeScripts() {
    // Reinitialize quick buy if it exists
    if (typeof window.initQuickBuy === 'function') {
      window.initQuickBuy();
    }
try {
      const newWishlistButtons = this.productGrid.querySelectorAll('.xb-wishlist__add');
      newWishlistButtons.forEach(btn => {
        if (!btn.hasAttribute('xb-add')) {
          btn.setAttribute('xb-add', 'true');
        }
      });
    } catch (error) {
      console.warn('Failed to add xb-add attribute:', error);
    }
    // Reinitialize any product card interactions
    if (typeof theme !== 'undefined' && theme.Product) {
      const newProducts = this.productGrid.querySelectorAll('.product-item');
      newProducts.forEach(product => {
        // Reinitialize product specific features
      });
    }
   
    // Trigger custom event for other scripts to hook into
    document.dispatchEvent(new CustomEvent('products:loaded', {
      detail: { 
        source: 'load-more',
        currentPage: this.currentPage,
        totalPages: this.totalPages,
        isLastPage: this.totalPages !== null ? (this.currentPage >= this.totalPages) : null
      }
   
    }));
  }
}

// Store instance globally to prevent duplicates
let loadMoreInstance = null;

// Initialize function
function initLoadMore() {
  console.log('Initializing Load More Pagination...');
  
  // Clean up old instance if exists
  if (loadMoreInstance) {
    console.log('Cleaning up old instance...');
    loadMoreInstance = null;
  }
  
  // Create new instance
  loadMoreInstance = new LoadMorePagination();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLoadMore);
} else {
  initLoadMore();
}

// IMPORTANT: Reinitialize after facet/filter form submissions
// Listen for facet updates (Shopify's standard event)
document.addEventListener('facet:updated', function() {
  console.log('Facet updated - reinitializing pagination...');
  setTimeout(initLoadMore, 100); // Small delay to ensure DOM is updated
});

// Listen for collection filter changes
document.addEventListener('collection:updated', function() {
  console.log('Collection updated - reinitializing pagination...');
  setTimeout(initLoadMore, 100);
});

// Observe changes to the product grid container
if (document.getElementById('CollectionProductGrid')) {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // If the main product grid was replaced, reinitialize
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        const hasProductGrid = Array.from(mutation.addedNodes).some(node => 
          node.id === 'main-collection-product-grid' || 
          (node.querySelector && node.querySelector('#main-collection-product-grid'))
        );
        
        if (hasProductGrid) {
          console.log('Product grid replaced - reinitializing pagination...');
          setTimeout(initLoadMore, 100);
        }
      }
    });
  });
  
  observer.observe(document.getElementById('CollectionProductGrid'), {
    childList: true,
    subtree: true
  });
}

// Reinitialize on page show (handles back/forward navigation)
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    // Page was loaded from cache, reload to show initial state
    console.log('Page shown from cache - reloading...');
    window.location.reload();
  }
});

// Handle browser back/forward buttons
window.addEventListener('popstate', () => {
  // When user navigates back/forward, reload page to initial state
  console.log('Browser navigation - reloading...');
  window.location.reload();
});

// Export for manual reinitialization if needed
window.reinitLoadMore = initLoadMore;
